import type { QueryClient } from "@tanstack/react-query"
import type { Page, Station, StationTarget } from "@/types"

const STATIONS_QUERY_KEY = "stations"

export const patchStationTargetInCache = (
  queryClient: QueryClient,
  stationId: string,
  targetPatch: Partial<StationTarget> & { id: string },
) => {
  queryClient.setQueriesData<Page<Station>>(
    { queryKey: [STATIONS_QUERY_KEY] },
    (old) => {
      if (!old?.items) return old

      return {
        ...old,
        items: old.items.map((station) => {
          if (station.id !== stationId) return station

          const previousTarget = station.target
          if (!previousTarget || previousTarget.id !== targetPatch.id) {
            return {
              ...station,
              target: {
                id: targetPatch.id,
                station_id: stationId,
                lat: targetPatch.lat ?? null,
                lng: targetPatch.lng ?? null,
                height_above_ground: targetPatch.height_above_ground ?? null,
                height_above_sea: targetPatch.height_above_sea ?? null,
                created_at: targetPatch.created_at,
                updated_at: targetPatch.updated_at ?? null,
              },
            }
          }

          return {
            ...station,
            target: {
              ...previousTarget,
              ...targetPatch,
            },
          }
        }),
      }
    },
  )

  queryClient.setQueryData<Station>(["station", stationId], (oldStation) => {
    if (!oldStation) return oldStation

    const previousTarget = oldStation.target
    if (!previousTarget || previousTarget.id !== targetPatch.id) {
      return {
        ...oldStation,
        target: {
          id: targetPatch.id,
          station_id: stationId,
          lat: targetPatch.lat ?? null,
          lng: targetPatch.lng ?? null,
          height_above_ground: targetPatch.height_above_ground ?? null,
          height_above_sea: targetPatch.height_above_sea ?? null,
          created_at: targetPatch.created_at,
          updated_at: targetPatch.updated_at ?? null,
        },
      }
    }

    return {
      ...oldStation,
      target: {
        ...previousTarget,
        ...targetPatch,
      },
    }
  })
}

export const invalidateStationsQueries = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({ queryKey: [STATIONS_QUERY_KEY] })
}

export const resolveStationIdForTarget = (
  queryClient: QueryClient,
  targetId: string,
): string | null => {
  const pages = queryClient.getQueriesData<Page<Station>>({
    queryKey: [STATIONS_QUERY_KEY],
  })

  for (const [, page] of pages) {
    const match = page?.items?.find(
      (station) => station.target?.id === targetId,
    )
    if (match) {
      return match.id
    }
  }

  const detailEntries = queryClient.getQueriesData<Station>({
    queryKey: ["station"],
  })

  for (const [, station] of detailEntries) {
    if (station?.target?.id === targetId) {
      return station.id
    }
  }

  return null
}
